// SettleWeek — the deterministic weekly pipeline per ARCHITECTURE.md §4.
// Internal helper; never dispatched directly by the UI. Triggered by AdvanceDay
// at end of in-game day 5.

import type { BuilderContent } from '../config/types.js';
import type { GameState, OutboundEvent } from '../state/types.js';
import { applyTxn } from '../ledger/append.js';
import { operateBusinessForWeek } from '../business/operate.js';
import { settleAssetYields } from '../assets/yield.js';
import { settleLiabilityUpkeep } from '../liabilities/upkeep.js';
import { settleLoanPayments } from '../loans/pay.js';
import { settleSavingsInterest } from '../savings/interest.js';
import { settleInvestorPayouts } from '../investing/payout.js';
import { landDueConsequences } from '../decisions/consequences.js';
import {
  settleServices,
  settleSocial,
  settleGaming,
  settleTrade,
  settleProperty,
  settleBroker,
} from '../ventures/settle.js';
import { findBusiness } from '../config/load.js';
import { cashflowForWeek } from '../ledger/selectors.js';
import { assessWin } from '../win/conditions.js';
import { nextWeighted } from '../rng/prng.js';

export type SettleResult = {
  state: GameState;
  outbound: OutboundEvent[];
};

export function settleWeek(state: GameState, content: BuilderContent): SettleResult {
  const settlingWeek = state.clock.week;
  const outbound: OutboundEvent[] = [];

  let s = state;

  // 1. Each business operates.
  for (const inst of state.businesses) {
    const def = findBusiness(content, inst.defId);
    if (!def) {
      // Programmer error: instance references unknown def. Skip rather than throw —
      // engine should remain deterministic even with stale instances.
      continue;
    }
    s = operateBusinessForWeek(s, inst, {
      def,
      reputation: s.profile.reputation,
      effects: s.weeklyEffects,
    });
  }

  // 1b. Other ventures earn alongside the business.
  s = settleServices(s);
  s = settleSocial(s);
  s = settleGaming(s);
  s = settleTrade(s);
  s = settleProperty(s);
  s = settleBroker(s);

  // 2. Assets yield.
  s = settleAssetYields(s);

  // 3. Liabilities upkeep (D5 — at settle).
  s = settleLiabilityUpkeep(s);

  // 4. Loan payments.
  s = settleLoanPayments(s);

  // 5. Savings interest.
  s = settleSavingsInterest(s);

  // 6. Investor profit-share — skim each investor's cut of this week's revenue.
  s = settleInvestorPayouts(s, settlingWeek);

  // 6b. Land any decision consequences whose week has arrived (delayed rewards/costs).
  s = landDueConsequences(s, settlingWeek);

  // 7. Living expenses.
  s = applyTxn(s, {
    type: 'living',
    amount: -content.tuning.livingExpensesPerWeek,
    category: 'expense',
    note: 'Living expenses',
  });

  // 8. Draw event card if the deck has any (MVP ships empty → no-op).
  if (content.events.length > 0) {
    const items = content.events.map((c) => ({ item: c, weight: c.weight }));
    const drawn = nextWeighted(s.rngSeed, s.rngCursor, items);
    s = {
      ...s,
      rngCursor: drawn.nextCursor,
      weeklyEffects: [
        ...s.weeklyEffects,
        {
          id: drawn.value.id,
          weeksRemaining: drawn.value.durationWeeks,
          effects: drawn.value.effects,
        },
      ],
    };
    outbound.push({ kind: 'EventCardDrawn', cardId: drawn.value.id });
  }

  // 9. Reputation recompute — Phase 2.

  // 10. Win check.
  const win = assessWin(s, settlingWeek);
  if (win.passed) {
    const newLevel = s.profile.level + 1;
    s = {
      ...s,
      profile: { ...s.profile, level: newLevel },
    };
    outbound.push({ kind: 'LevelWon', level: settlingWeek });
    outbound.push({ kind: 'LevelUp', newLevel });
  }

  // 11. Emit the weekly settlement event with the cashflow this week, then advance the clock.
  const cashflow = cashflowForWeek(s, settlingWeek);
  outbound.push({ kind: 'WeekSettled', week: settlingWeek, cashflow });

  // Tick down transient effects, drop any whose duration just hit zero.
  const nextEffects = s.weeklyEffects
    .map((e) => ({ ...e, weeksRemaining: e.weeksRemaining - 1 }))
    .filter((e) => e.weeksRemaining > 0);

  s = {
    ...s,
    weeklyEffects: nextEffects,
    clock: {
      ...s.clock,
      week: s.clock.week + 1,
      dayOfWeek: 1,
    },
  };

  return { state: s, outbound };
}
